import { useState } from 'react';
import { inspectStoredFirmware, Result } from '../../api/api';
import { IFirmwareFileInspection } from '../../api/types';
import { FirmwareInspectionPanel } from './FirmwareInspectionPanel';

interface IParams {
  productCode: string;
  variant: string;
  firmwareVersion: string;
}

export function StoredFirmwareInspection({
  productCode,
  variant,
  firmwareVersion,
}: IParams) {
  return (
    <StoredFirmwareInspectionContent
      key={`${productCode}:${variant}:${firmwareVersion}`}
      productCode={productCode}
      variant={variant}
      firmwareVersion={firmwareVersion}
    />
  );
}

function StoredFirmwareInspectionContent({
  productCode,
  variant,
  firmwareVersion,
}: IParams) {
  const [inspection, setInspection] = useState<
    Result<IFirmwareFileInspection>
  >({ status: '' });
  const [isInspecting, setIsInspecting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  async function handleInspect() {
    if (isInspecting) return;

    if (inspection.status === 'OK') {
      setIsOpen((open) => !open);
      return;
    }

    setIsInspecting(true);
    setInspection({ status: 'Loading' });
    try {
      const result = await inspectStoredFirmware(
        productCode,
        variant,
        firmwareVersion,
      );
      setInspection(result);
      setIsOpen(result.status === 'OK');
    } finally {
      setIsInspecting(false);
    }
  }

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={handleInspect}
        disabled={isInspecting}
        aria-busy={isInspecting}
        aria-expanded={isOpen}
        title="View parsed image metadata from the stored .dat file"
        className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          isInspecting
            ? 'cursor-wait border-slate-200 bg-slate-100 text-slate-500'
            : 'border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100'
        }`}
      >
        {isInspecting ? (
          <svg
            aria-hidden="true"
            className="h-4 w-4 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              d="M4 12a8 8 0 0 1 8-8"
              fill="currentColor"
            />
          </svg>
        ) : (
          <svg
            aria-hidden="true"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="M2.25 12s3.5-5.25 9.75-5.25S21.75 12 21.75 12s-3.5 5.25-9.75 5.25S2.25 12 2.25 12Z"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.7"
            />
            <circle cx="12" cy="12" r="2.5" strokeWidth="1.7" />
          </svg>
        )}
        <span>
          {isInspecting
            ? 'Loading image details...'
            : isOpen
              ? 'Hide image details'
              : 'View image details'}
        </span>
        {!isInspecting && (
          <svg
            aria-hidden="true"
            className={`h-4 w-4 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              d="m6 9 6 6 6-6"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.8"
            />
          </svg>
        )}
      </button>
      {inspection.status === 'OK' && isOpen && (
        <div className="mt-3">
          <FirmwareInspectionPanel inspection={inspection.data} />
        </div>
      )}
      {inspection.status !== '' &&
        inspection.status !== 'OK' &&
        inspection.status !== 'Loading' && (
          <p
            className="mt-3 text-red-600 dark:text-red-400"
            role="alert"
          >
            {inspection.status}: {inspection.errorMessages.join(', ')}
          </p>
        )}
    </div>
  );
}
